/***
 * Created on 2025/7/15
 * @author: Adam Cheng <adamcheng@qnap.com>
 ***/
import React, {useEffect, useRef, useState} from 'react';
import {useTranslation} from "react-i18next";

function MultiSelectInputComponent({placeholder_text, selectedItems, setSelectedItems, disabled=false, component_style, callbackDispatch, refresh}) {
    const { t } = useTranslation();
    const node = useRef();
    const stayOpenNode = useRef();
    const [open, setOpen] = useState('hide');
    const [numSelected, setNumSelected] = useState(selectedItems?.filter(i => i.checked)?.length || 0);

    const toggle = (e) => {
        if (disabled || (
            node.current &&
            !node.current.contains(e.target) &&
            !stayOpenNode.current.contains(e.target)
        )) {
            setOpen('hide');
        }
        else {
            if ((stayOpenNode.current && stayOpenNode.current.contains(e.target)) || e.target.classList.contains('stay-open')) {
                return; // Stay open if clicked inside the dropdown
            }
            else{
                setOpen((prevOpen) => prevOpen === 'hide' ? '' : 'hide');
            }
        }
    }

    const executeCallback = (action, payload, extra={}) => {
        if (callbackDispatch && typeof callbackDispatch === 'function') {
            callbackDispatch(action, payload, extra);
        }
    }

    const checkAllItems = () => {
        setSelectedItems(prevItems => {
            const res = prevItems.map(item => ({...item, checked: true}))
            setNumSelected(selectedItems?.length);
            executeCallback(
                'selectAll',
                [],
                {full_list: res});
            return res;
        });
    }

    const uncheckAllItems = () => {
        setSelectedItems(prevItems => {
            const res = prevItems.map(item => ({...item, checked: item?.locked || false}))
            setNumSelected(res.filter(i => i.checked).length);
            executeCallback(
                'deselectAll',
                [],
                {full_list: res});
            return res;
        });
    }

    const onCheckboxChange = (e, item) => {
        if (item?.locked) return; // locked items (e.g. devices derived from a selected Site) cannot be toggled
        let isChecked;
        if (e.target.tagName === 'INPUT') {
            isChecked = e.target.checked;
        }
        else {
            isChecked = !e.target.previousSibling.firstChild.checked;
        }
        setSelectedItems(prevItems => {
            const res = prevItems.map(i => i.value === item.value ? {...i, checked: isChecked} : i)
            setNumSelected(res.filter(i => i.checked).length);
            executeCallback(
                'onCheckboxChange',
                [item.value, isChecked],
                {full_list: res});
            return res;
        });
    }

    const onRemoveTag = (e, item) => {
        e.stopPropagation();
        setSelectedItems(prevItems => {
            const res = prevItems.map(i => i.value === item.value ? {...i, checked: false} : i);
            // numSelected.current = res.filter(i => i.checked).length;
            setNumSelected(res.filter(i => i.checked).length);
            executeCallback(
                'onCheckboxChange',
                [item.value, false],
                {full_list: res});
            return res;
        });
    }

    useEffect(() => {
        document.addEventListener("click", toggle)

        return () => {
            document.removeEventListener("click", toggle);
        }
    }, [disabled]);

    useEffect(() => {
        setNumSelected(selectedItems?.filter(i => i.checked)?.length || 0);
    }, [refresh]);

    return (
        <div className="comp-select comp-multi-select" style={component_style}>
            <div className="select-toggle" ref={node}>
                <div className={`select-toggle__wrapper ${open==='hide' ? '' : 'wrap'}`} >
                    { (numSelected <= 0 || disabled) &&
                        <span className="placeholder">{placeholder_text}</span>
                    }
                    {
                        (numSelected> 0 && !disabled) &&
                        selectedItems.filter(i => i.checked).slice(0, open==='hide' ? 3 : numSelected).map((item, index) => (
                            <span className="tag tag-tiny stay-open" key={item?.value?.id} disabled={item?.locked}>
                                {/*onClick={e => onCheckboxChange(e, item)} */}
                                <span className="tag-label">{item?.value?.name}</span>
                                <span className="close stay-open" onClick={e => onRemoveTag(e, item)}></span>
                            </span>
                        ))
                    }
                    {!disabled &&
                        <span className="tag tag-tiny overflow" style={{display:  numSelected> 3 && open==='hide' ? '' : 'none'}}>
                            <span className="tag-label">
                                { numSelected> 3 ? `+${numSelected- 3}` : ''}
                            </span>
                        </span>
                    }
                </div>
            </div>
            <div className={`comp-dropdown comp-select-dropdown ${open}`}>
                <ul className="comp-dropdown-menu add-scroll--medium" ref={stayOpenNode}>
                    <li className="section-sticky">
                        <div className="comp-checkall">
                            <span className="check-all-items" onClick={checkAllItems} disabled={selectedItems?.every(i => i.checked)}>{t('@Check all@')}</span>
                            <span className="section-sticky-divide"></span>
                            <span className="uncheck-all-items" onClick={uncheckAllItems} disabled={!selectedItems?.some(i => i.checked) || selectedItems.every(i => i.checked === i.locked)}>{t('@Uncheck all@')}</span>
                        </div>
                    </li>
                    {selectedItems.map((item, index) => (
                         <li className="section" key={item?.value?.id}>
                             <div className="section-items selection" disabled={item?.locked}>
                                 <div className="comp-checkbox-group">
                                     <div className="comp-checkbox no-label">
                                         <input type="checkbox" id={item?.value?.id} checked={item?.checked} disabled={item?.locked} onChange={e => onCheckboxChange(e, item)} />
                                         <label htmlFor={item?.value?.id} className="comp-label"></label>
                                     </div>
                                     <div className="val-field pl-16 text-no-ellipsis" onClick={e => onCheckboxChange(e, item)}>{item?.value?.name}</div>
                                 </div>
                             </div>
                        </li>
                    ))}
                </ul>
            </div>
        </div>
    );
}

export default MultiSelectInputComponent;